import { useState } from 'react';
import { Container, Title, Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
} from '@/hooks/api/useDepartments';
import { DepartmentsTable } from './components/DepartmentsTable';
import { DepartmentForm } from './components/DepartmentForm';
import type { Department } from '@/types/models';
import type { DepartmentFormValues } from './schemas/departmentSchema';

export function DepartmentsPage() {
  const { organizationId } = useOrganization();
  const { data: departments = [], isLoading } = useDepartments(organizationId);
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();

  const [formOpened, setFormOpened] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleCreate = () => {
    setSelectedDepartment(null);
    setIsEditing(false);
    setFormOpened(true);
  };

  const handleEdit = (department: Department) => {
    setSelectedDepartment(department);
    setIsEditing(true);
    setFormOpened(true);
  };

  const handleDelete = (department: Department) => {
    modals.openConfirmModal({
      title: 'Delete Department',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{department.name}</strong>? This action cannot be
          undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteDepartment.mutate(department.id);
      },
    });
  };

  const handleSubmit = async (values: DepartmentFormValues, keepOpen = false) => {
    if (!organizationId) return;

    if (isEditing && selectedDepartment) {
      await updateDepartment.mutateAsync({
        id: selectedDepartment.id,
        data: values,
      });
    } else {
      await createDepartment.mutateAsync({
        ...values,
        organization_id: organizationId,
      });
    }

    if (!keepOpen) {
      setFormOpened(false);
      setSelectedDepartment(null);
    }
  };

  return (
    <Container fluid>
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={1}>Departments</Title>
            <Text c="dimmed" size="sm">
              Manage organizational departments
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Add Department
          </Button>
        </Group>

        <DepartmentsTable
          departments={departments}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <DepartmentForm
          opened={formOpened}
          onClose={() => {
            setFormOpened(false);
            setSelectedDepartment(null);
          }}
          onSubmit={handleSubmit}
          department={isEditing ? selectedDepartment : null}
          isLoading={createDepartment.isPending || updateDepartment.isPending}
        />
      </Stack>
    </Container>
  );
}
